import type { FC, ReactNode } from 'react'

import { useCallback, useState } from 'react'
import {
    Ban,
    Eye,
    Lock,
    MessageSquareText,
    SlidersHorizontal,
    UserRound
} from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CtaBand } from '@/components/generals/cta-band'
import { Eyebrow } from '@/components/generals/eyebrow'
import { PageHero } from '@/components/generals/page-hero'
import { Reveal } from '@/components/generals/reveal'
import { SectionHeading } from '@/components/generals/section-heading'
import { SplitHeading } from '@/components/generals/split-heading'
import { AiDemo } from '@/components/landing/ai/ai-demo'
import { PredictionChart } from '@/components/landing/ai/prediction-chart'
import { SUGGESTED_QUESTIONS } from '@/lib/ai-demo'
import { LANDSCAPES } from '@/lib/visual'

import {
    AI_FEATURES,
    FeatureGrid,
    ProgressLine,
    useSeenSet
} from '@/components/landing/ai/feature-grid'

const FEATURE_DETAILS = [
    ['Plain questions, no menus to learn', 'Replies built on your actual balances and bills', 'It remembers what you asked a moment ago'],
    ['Forecasts that show their margin of error', 'A heads-up before a lean week arrives', 'A single useful next step, no lectures'],
    ['Filed as soon as a payment clears', 'Fix a label once and it remembers', 'Budgets that stay true without upkeep']
]

const STEPS = [
    { title: 'It finds your patterns', body: 'Payday, rent day, the Friday pizza. Within a few weeks Verdane picks up the rhythms already sitting in your accounts.' },
    { title: 'It plays out the months ahead', body: 'Your history and your scheduled bills feed thousands of simulated months, so each forecast arrives with a realistic range.' },
    { title: 'It speaks up when it counts', body: 'If a lean week or a strange charge looks likely, you hear about it in good time, along with one simple step to take.' },
    { title: 'It improves as you go', body: 'Every month of history makes the next forecast tighter, and you can set it straight any time it misreads something.' }
]

const PRIVACY = [
    { icon: Lock, title: 'Locked at every step', body: 'Stored under 256-bit encryption and sent only over TLS, whether your data is resting or on the move.' },
    { icon: Eye, title: 'Read-only, always', body: 'Verdane reads your accounts but cannot move a cent until you say yes yourself.' },
    { icon: Ban, title: 'Yours, not for sale', body: 'We do not sell your data, and we never use it to put ads in front of you. That will not change.' },
    { icon: SlidersHorizontal, title: 'Your call, every time', body: 'Unlink an account, download all of it or erase your data whenever you choose, in a few clicks.' },
    { icon: MessageSquareText, title: 'Reasoning on show', body: 'Each insight lays out the figures it came from, so you can trace exactly how it got there.' },
    { icon: UserRound, title: 'Humans close by', body: 'Certified advisors check how the AI handles tricky cases, and a real person is only a tap away.' }
]

const AiIntelligence: FC = (): ReactNode => {
    const { seen, markSeen } = useSeenSet()
    const [incoming, setIncoming] = useState<{ text: string, nonce: number } | null>(null)

    const askDemo = useCallback((text: string) => {
        setIncoming({ text, nonce: Date.now() })
        document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, [])

    return (
        <Layout>
            <Seo
                title='AI Intelligence'
                description='Meet the Verdane AI advisor. Ask about your money in everyday words, see the weeks ahead before they arrive and keep your data your own. Try the demo.'
            />

            <PageHero
                eyebrow='Verdane AI'
                title={'An advisor that hears you\nand *thinks ahead*'}
                subtitle='It reads your accounts, your bills and your goals, then replies the way a careful planner would. Start a conversation below with a sample household.'
                image={LANDSCAPES.meadowBlur}
                position='center 60%'
            />

            <section id='demo' aria-label='Talk to the AI advisor' className='scroll-mt-24 pb-28'>
                <div className='shell'>
                    <Reveal y={50}>
                        <AiDemo incoming={incoming} />
                    </Reveal>
                </div>
            </section>

            <section id='features' aria-label='What the AI can do' className='relative py-24 sm:py-32'>
                <div className='shell'>
                    <SectionHeading
                        eyebrow='How it thinks'
                        title='Three skills, *one picture*'
                        subtitle='Talking, forecasting and sorting all draw on the same view of your money, so every one of them sharpens the other two.'
                    >
                        <ProgressLine seen={seen} total={AI_FEATURES.length} className='mt-10' />
                    </SectionHeading>

                    <FeatureGrid onVisible={markSeen} className='mt-14' />

                    <div className='mt-6 grid gap-5 md:grid-cols-3 lg:gap-6'>
                        {FEATURE_DETAILS.map((list, index) => (
                            <Reveal as='ul' key={index} delay={index * 140} className='space-y-2.5 px-2'>
                                {list.map(item => (
                                    <li key={item} className='flex items-start gap-3 text-sm text-cream/70'>
                                        <span aria-hidden='true' className='mt-2 size-1 shrink-0 rounded-full bg-citron' />
                                        {item}
                                    </li>
                                ))}
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section id='predictions' aria-labelledby='predictions-title' className='relative py-24 sm:py-32'>
                <div className='shell grid items-center gap-14 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-20'>
                    <div>
                        <Eyebrow align='left'>
                            Behind the forecast
                        </Eyebrow>

                        <SplitHeading
                            id='predictions-title'
                            text='The road ahead, *honestly drawn*'
                            className='mt-6 text-[2.5rem] font-normal leading-[1] tracking-[-0.045em] text-cream sm:text-6xl'
                        />

                        <ol className='mt-10 space-y-7'>
                            {STEPS.map((step, index) => (
                                <Reveal as='li' key={step.title} delay={index * 120} className='flex gap-5'>
                                    <span className='font-serif text-3xl italic leading-none text-citron'>
                                        0{index + 1}
                                    </span>

                                    <div>
                                        <h3 className='text-lg tracking-[-0.02em] text-cream'>
                                            {step.title}
                                        </h3>

                                        <p className='mt-1.5 max-w-md text-[15px] leading-relaxed text-cream/60'>
                                            {step.body}
                                        </p>
                                    </div>
                                </Reveal>
                            ))}
                        </ol>
                    </div>

                    <Reveal x={50} y={20}>
                        <PredictionChart />
                    </Reveal>
                </div>
            </section>

            <section id='privacy' aria-label='Privacy and security' className='relative scroll-mt-24 py-24 sm:py-32'>
                <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-10 h-[500px] bg-[radial-gradient(40%_55%_at_50%_0%,rgba(143,174,98,0.14),transparent_70%)]' />

                <div className='shell relative'>
                    <SectionHeading
                        eyebrow='Privacy, built in'
                        title='What is yours *stays yours*'
                        subtitle='Advice only works when you can trust where your data goes. These six promises shape everything we build.'
                    />

                    <ul className='mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                        {PRIVACY.map((item, index) => (
                            <Reveal as='li' key={item.title} delay={(index % 3) * 120} className='glass grain relative overflow-hidden rounded-[28px] p-7'>
                                <span className='grid size-11 place-items-center rounded-full bg-citron/10 text-citron ring-1 ring-citron/20'>
                                    <item.icon aria-hidden='true' className='size-5' strokeWidth={1.6} />
                                </span>

                                <h3 className='mt-6 font-serif text-2xl italic text-cream'>
                                    {item.title}
                                </h3>

                                <p className='mt-2 text-sm leading-relaxed text-cream/60'>
                                    {item.body}
                                </p>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <section id='questions' aria-label='Suggested questions' className='relative pb-28 pt-8'>
                <div className='shell'>
                    <SectionHeading
                        eyebrow='Try asking'
                        title='Need a place *to begin?*'
                        subtitle='Choose a question and the advisor above will reply.'
                    />

                    <Reveal className='mx-auto mt-12 flex max-w-4xl flex-wrap justify-center gap-2.5'>
                        {SUGGESTED_QUESTIONS.map(question => (
                            <button
                                key={question}
                                type='button'
                                onClick={() => askDemo(question)}
                                className='glass rounded-full px-5 py-3 text-sm text-cream/80 transition-all duration-300 hover:-translate-y-0.5 hover:text-cream'
                            >
                                {question}
                            </button>
                        ))}
                    </Reveal>
                </div>
            </section>

            <CtaBand
                eyebrow='Make it personal'
                title={'Now ask about\n*your own* money'}
                image={LANDSCAPES.pampas}
                position='center 40%'
            />
        </Layout>
    )
}

export default AiIntelligence
